/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import {
  fromBoolean,
  fromDate,
  fromNumber,
  sanitizeAddressData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toNumber
} from "../formServiceFunctions";
import _forEach from "lodash/forEach";
import _set from "lodash/set";
import _get from "lodash/get";
import { getProductData } from "../productService";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { gaEvent } from "../googleAnalyticsService";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_DIVORCE]: {
      divorce: ["dateMarriage", "cityMarriage", "numberChildren", "statusDivorce", "dateSeparation", "movedOut", "dateMovedOut"],
    },
    [applicationStatuses.APPLICATION_STATUS_FINANCIAL]: {
      divorce: ["netIncomePetitioner", "netIncomePartner"],
    },
    [applicationStatuses.APPLICATION_STATUS_PENSIONS]: {
      divorce: ["pensionsExcluded", "numberPensions", "numberPensionsPartner", "pensionsQuestions"],
    },
    [applicationStatuses.APPLICATION_STATUS_PROCESS_COST_SUPPORT]: {
      divorce: ["hasProcessCostSupport"],
    },
    [applicationStatuses.APPLICATION_STATUS_PARTNER]: {
      partner: ["gender", "givenName", "familyName", "nationality"],
      "partner.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL]: {
      customer: ["telephone", "contactTime"],
      client: ["gender", "givenName", "familyName", "nationality"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      commonAddress: ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_SUMMARY]: {
      divorce: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      divorce: ["powerOfAttorney"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {
      divorce: [],
      children: [],
    },
  });

  conversionFields = [
    {
      path: "dateMarriage",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "dateSeparation",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "dateMovedOut",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "numberChildren",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "netIncomePetitioner",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "netIncomePartner",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "pensionsExcluded",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "numberPensions",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "numberPensionsPartner",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "hasProcessCostSupport",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
  ];

  statusFunctions: { [k in ApplicationStatus]?: () => void } = {
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: () => {
      gaEvent({
        category: "Document Upload",
        action: "Divorce Documents uploaded",
      });
    },
  };

  save = async ({ formData, applicationStatus, nextApplicationStatus, product, customer, updateMutation }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};

    const resultData = this.handleDivorceFields(formData);
    if (_has(fields, "customer")) {
      const customerData = _pick(resultData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client")) {
      let clientData = _pick(resultData.client, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({ uri: "/people", id: product.client.id, data: clientData });
    }

    if (_has(fields, "client.residenceAddress")) {
      let addressData = _pick(resultData.client.residenceAddress, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "partner")) {
      let partnerData = _pick(resultData.partner, fields.partner);
      partnerData = sanitizePersonData(partnerData);
      await updateMutation.mutateAsync({ uri: "/people", id: product.partner.id, data: partnerData });
    }

    if (_has(fields, "partner.residenceAddress")) {
      let addressData = _pick(resultData.partner.residenceAddress, fields["partner.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.partner.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "commonAddress")) {
      let addressData = _pick(resultData.commonAddress, fields["commonAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.commonAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "children")) {
      for (let i = 0; i < product.children.length; i++) {
        await updateMutation.mutateAsync({
          uri: "/children",
          id: product.children[i].id,
          data: {
            name: resultData.children[i].name,
            livesWith: resultData.children[i].livesWith,
          },
        });
      }
    }

    let divorceUpdate: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      divorceUpdate.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, "divorce")) {
      divorceUpdate = _merge(divorceUpdate, _pick(resultData, fields.divorce));
    }

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: divorceUpdate,
    });
  };

  handleDivorceFields = (data: FormServiceSaveProps<ApplicationStatus>["formData"]) => {
    let resultData = _merge({}, data);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertTo } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertTo(_get(resultData, path)));
      }
    });

    if (resultData.movedOut === "nobody" || resultData.dateMovedOut === "") {
      resultData.dateMovedOut = null;
    }

    if (_has(resultData, "powerOfAttorney")) {
      resultData.powerOfAttorney = new Date();
    }

    return resultData;
  };

  getAvailableSteps = (formData: Record<string, any>) => {
    let availableSteps = [];

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DIVORCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_FINANCIAL]);
    if (!formData.shortMarriage) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PENSIONS]);
    }
    if (formData.netIncomePetitioner <= 1200 && formData.paymentType !== "insurance") {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PROCESS_COST_SUPPORT]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PARTNER]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
